import { FC, MouseEvent, ReactElement, useCallback, useMemo, useState } from "react";
import { Box, Button, Checkbox, Divider, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { FilterList, TuneRounded as TuneIcon } from "@mui/icons-material";

import { DialogMenu } from "@/components";
import MultiSelectFilter from "./MultiSelectFilter";
import { tokens } from "@/tokens";
import { AUTHORITY_BUCKETS, FindingSign, TariffAnalysisAuthority } from "./tariffAnalysisAggregation";

interface TariffAnalysisFiltersProps {
  manufacturers: string[];
  originCodes: string[];
  htsCodes: string[];
  signs: FindingSign[];
  authorities: TariffAnalysisAuthority[];
  hasCh99: boolean;
  metalSplitOnly: boolean;
  minAbsDifference: string;
  maxAbsDifference: string;
  onApply: (filters: {
    manufacturers: string[];
    originCodes: string[];
    htsCodes: string[];
    signs: FindingSign[];
    authorities: TariffAnalysisAuthority[];
    hasCh99: boolean;
    metalSplitOnly: boolean;
    minAbsDifference: string;
    maxAbsDifference: string;
  }) => void;
  manufacturerOptions: Array<{ value: string; label: string }>;
  countryOptions: Array<{ value: string; label: string }>;
  htsOptions: Array<{ value: string; label: string }>;
  // Per-authority { count, dollars } so the Authority filter can show
  // amounts inline next to each option.
  authorityAmounts?: Map<string, { count: number; dollars: number }>;
}

interface LocalState {
  manufacturers: string[];
  originCodes: string[];
  htsCodes: string[];
  signs: FindingSign[];
  authorities: TariffAnalysisAuthority[];
  hasCh99: boolean;
  metalSplitOnly: boolean;
  minAbsDifference: string;
  maxAbsDifference: string;
}

const TariffAnalysisFilters: FC<TariffAnalysisFiltersProps> = ({
  manufacturers,
  originCodes,
  htsCodes,
  signs,
  authorities,
  hasCh99,
  metalSplitOnly,
  minAbsDifference,
  maxAbsDifference,
  onApply,
  manufacturerOptions,
  countryOptions,
  htsOptions,
  authorityAmounts,
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [local, setLocal] = useState<LocalState>({
    manufacturers: [],
    originCodes: [],
    htsCodes: [],
    signs: [],
    authorities: [],
    hasCh99: false,
    metalSplitOnly: false,
    minAbsDifference: "",
    maxAbsDifference: "",
  });

  const signOptions = useMemo(
    () => [
      { value: "savings", label: "Savings" },
      { value: "risk", label: "Risk" },
    ],
    []
  );

  const fmtDollars = (n: number) => {
    if (!Number.isFinite(n) || n === 0) return "$0";
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${Math.round(n)}`;
  };

  const authorityOptions = useMemo(
    () =>
      AUTHORITY_BUCKETS.map((authority) => {
        const meta = authorityAmounts?.get(authority);
        return {
          value: authority,
          label: authority,
          meta: meta ? `${fmtDollars(meta.dollars)} · ${meta.count} findings` : undefined,
        };
      }),
    [authorityAmounts]
  );

  const openFilterModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      setLocal({
        manufacturers,
        originCodes,
        htsCodes,
        signs,
        authorities,
        hasCh99,
        metalSplitOnly,
        minAbsDifference,
        maxAbsDifference,
      });
      setAnchorEl(event.currentTarget);
    },
    [
      manufacturers,
      originCodes,
      htsCodes,
      signs,
      authorities,
      hasCh99,
      metalSplitOnly,
      minAbsDifference,
      maxAbsDifference,
    ]
  );

  const closeFilterModal = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const applyFilters = (): void => {
    onApply(local);
    closeFilterModal();
  };

  const resetFilters = useCallback((): void => {
    setLocal({
      manufacturers: [],
      originCodes: [],
      htsCodes: [],
      signs: [],
      authorities: [],
      hasCh99: false,
      metalSplitOnly: false,
      minAbsDifference: "",
      maxAbsDifference: "",
    });
  }, []);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    if (manufacturers.length > 0) count++;
    if (originCodes.length > 0) count++;
    if (htsCodes.length > 0) count++;
    if (signs.length > 0) count++;
    if (authorities.length > 0) count++;
    if (hasCh99) count++;
    if (metalSplitOnly) count++;
    if (minAbsDifference) count++;
    if (maxAbsDifference) count++;
    return count;
  }, [manufacturers, originCodes, htsCodes, signs, authorities, hasCh99, metalSplitOnly, minAbsDifference, maxAbsDifference]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={openFilterModal}
        startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
        sx={{
          fontWeight: tokens.fontWeight.bold,
          fontSize: tokens.fontSize.md,
          borderRadius: tokens.radius.md,
          px: 2,
          py: 0.75,
          color: tokens.text.body,
          borderColor: tokens.border.default,
          bgcolor: tokens.bg.surface,
          "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          flex: { xs: 1, sm: "initial" },
        }}
      >
        Filters
        {appliedFilterCount > 0 && (
          <Box
            sx={{
              ml: 0.75,
              width: 20,
              height: 20,
              bgcolor: "rgba(11,73,75,0.1)",
              color: tokens.brand.primary,
              display: "grid",
              placeContent: "center",
              borderRadius: "50%",
              fontSize: tokens.fontSize["2xs"],
              fontWeight: tokens.fontWeight.heavy,
            }}
          >
            {appliedFilterCount}
          </Box>
        )}
      </Button>
      <DialogMenu
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeFilterModal}
        sx={{
          "& .MuiPaper-root": {
            width: 440,
            maxHeight: "85vh",
            borderRadius: "12px",
            boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          sx={{ borderBottom: `1px solid ${tokens.border.default}` }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: tokens.radius.md,
                bgcolor: tokens.bg.muted,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterList sx={{ fontSize: 20, color: tokens.brand.primary }} />
            </Box>
            <Typography
              variant="h6"
              fontSize={16}
              fontWeight={tokens.fontWeight.bold}
              color={tokens.text.primary}
            >
              Filters
            </Typography>
          </Stack>

          <Button
            variant="text"
            size="small"
            onClick={resetFilters}
            sx={{
              color: tokens.brand.primary,
              fontWeight: tokens.fontWeight.semibold,
              fontSize: tokens.fontSize.base,
              "&:hover": { backgroundColor: tokens.bg.muted },
            }}
          >
            Clear All
          </Button>
        </Stack>

        {/* Filter Fields */}
        <Box sx={{ maxHeight: "55vh", overflow: "auto" }}>
          <Stack spacing={2.5} p={3}>
            <MultiSelectFilter
              label="Manufacturers"
              options={manufacturerOptions}
              value={local.manufacturers}
              onChange={(newValue) =>
                setLocal((prev) => ({ ...prev, manufacturers: newValue }))
              }
              placeholder="Select manufacturers..."
            />
            <MultiSelectFilter
              label="Country of Origin"
              options={countryOptions}
              value={local.originCodes}
              onChange={(newValue) =>
                setLocal((prev) => ({ ...prev, originCodes: newValue }))
              }
              placeholder="Select countries..."
            />
            <MultiSelectFilter
              label="HTS Code"
              options={htsOptions}
              value={local.htsCodes}
              onChange={(newValue) =>
                setLocal((prev) => ({ ...prev, htsCodes: newValue }))
              }
              placeholder="Select HTS codes..."
            />
            <MultiSelectFilter
              label="Finding Sign"
              options={signOptions}
              value={local.signs}
              onChange={(newValue) =>
                setLocal((prev) => ({ ...prev, signs: newValue as FindingSign[] }))
              }
              placeholder="Savings or risk..."
            />
            <MultiSelectFilter
              label="Authority"
              options={authorityOptions}
              value={local.authorities}
              onChange={(newValue) =>
                setLocal((prev) => ({ ...prev, authorities: newValue as TariffAnalysisAuthority[] }))
              }
              placeholder="Select authorities..."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                type="number"
                label="Min $"
                value={local.minAbsDifference}
                onChange={(event) =>
                  setLocal((prev) => ({ ...prev, minAbsDifference: event.target.value }))
                }
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Max $"
                value={local.maxAbsDifference}
                onChange={(event) =>
                  setLocal((prev) => ({ ...prev, maxAbsDifference: event.target.value }))
                }
                fullWidth
              />
            </Stack>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={local.hasCh99}
                    onChange={(event) =>
                      setLocal((prev) => ({ ...prev, hasCh99: event.target.checked }))
                    }
                    sx={{ color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }}
                  />
                }
                label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.body }}>Has Ch99</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={local.metalSplitOnly}
                    onChange={(event) =>
                      setLocal((prev) => ({ ...prev, metalSplitOnly: event.target.checked }))
                    }
                    sx={{ color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }}
                  />
                }
                label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.body }}>Metal split only</Typography>}
              />
            </Stack>
          </Stack>
        </Box>

        <Divider />

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          px={3}
          py={2}
          sx={{ background: tokens.bg.hover }}
        >
          <Button
            variant="outlined"
            sx={{
              flex: 1,
              fontWeight: tokens.fontWeight.bold,
              fontSize: tokens.fontSize.md,
              borderRadius: tokens.radius.md,
              borderColor: tokens.border.default,
              color: tokens.text.body,
              "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.surface },
            }}
            onClick={closeFilterModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              flex: 1,
              fontWeight: tokens.fontWeight.bold,
              fontSize: tokens.fontSize.md,
              borderRadius: tokens.radius.md,
              bgcolor: tokens.brand.primary,
              "&:hover": { bgcolor: tokens.brand.primaryHover },
            }}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </Stack>
      </DialogMenu>
    </>
  );
};

export default TariffAnalysisFilters;
